import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, Clock, MapPin, Save, NotebookPen, UserCircle, Crown, ChevronDown, ChevronUp, Bell } from "lucide-react";
import { TeamAvatar } from "../components/TeamAvatar";
import { useServerSyncedTimer } from "../hooks/useServerSyncedTimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const noteKey = (groupNumber, participantId) => `${groupNumber}::${participantId || "GROUP"}`;

const progressStatus = (p) => {
  const ap = p.answer_progress || { answered: 0, expected: 0 };
  if (ap.expected === 0) return { level: "none", label: "No questions" };
  if (ap.answered === 0) return { level: "none", label: `0 / ${ap.expected}` };
  if (ap.answered < ap.expected) return { level: "partial", label: `${ap.answered} / ${ap.expected}` };
  return { level: "done", label: `${ap.answered} / ${ap.expected} ✓` };
};

const dotClass = (level) => {
  switch (level) {
    case "done": return "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.6)]";
    case "partial": return "bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.55)]";
    default: return "bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.55)]";
  }
};

const StatusDot = ({ level }) => (
  <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotClass(level)}`} aria-hidden="true" />
);

const HostStationView = () => {
  const { hostKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [drafts, setDrafts] = useState({}); // key -> text
  const [saving, setSaving] = useState({}); // key -> bool
  const [tab, setTab] = useState("group"); // "group" | "participants"
  const [instructionsOpen, setInstructionsOpen] = useState(true);
  const [nudging, setNudging] = useState({}); // participant_id -> bool (cooldown)
  const dirtyRef = useRef(new Set());

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/host/${hostKey}`);
      setData(res.data);
      // Hydrate drafts for any keys NOT currently being edited
      setDrafts((prev) => {
        const next = { ...prev };
        const noteByKey = {};
        (res.data.notes || []).forEach((n) => {
          noteByKey[noteKey(n.group_number, n.participant_id)] = n.note_text || "";
        });
        // For every visible scope (group + each participant) set draft from server unless dirty
        (res.data.groups_here || []).forEach((g) => {
          const gKey = noteKey(g.number, null);
          if (!dirtyRef.current.has(gKey)) next[gKey] = noteByKey[gKey] || "";
          (g.participants || []).forEach((p) => {
            const pKey = noteKey(g.number, p.id);
            if (!dirtyRef.current.has(pKey)) next[pKey] = noteByKey[pKey] || "";
          });
        });
        return next;
      });
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load host view");
    } finally {
      setLoading(false);
    }
  }, [hostKey]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 4000);
    return () => clearInterval(id);
  }, [fetchData]);

  // Re-render every second for countdown
  const timer = useServerSyncedTimer(data?.session);
  const timerText = timer.active ? timer.mmss : null;

  const onDraftChange = (key, value) => {
    dirtyRef.current.add(key);
    setDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const saveNote = async (groupNumber, participantId, participantName) => {
    const key = noteKey(groupNumber, participantId);
    const text = drafts[key] ?? "";
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await axios.post(`${API}/host/${hostKey}/notes`, {
        group_number: groupNumber,
        participant_id: participantId,
        note_text: text,
        author: "Host",
      });
      dirtyRef.current.delete(key);
      toast.success(participantId ? `Note saved for ${participantName || "participant"}` : `Group ${groupNumber} note saved`);
    } catch (e) {
      toast.error("Failed to save note");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const nudgeParticipant = async (participantId, participantName) => {
    if (nudging[participantId]) return;
    setNudging((s) => ({ ...s, [participantId]: true }));
    try {
      await axios.post(`${API}/host/${hostKey}/nudge`, { participant_id: participantId });
      toast.success(`Nudge sent to ${participantName?.trim() || "participant"} ✨`);
    } catch (e) {
      toast.error("Failed to send nudge");
    } finally {
      // 6s cooldown so the host can't accidentally spam
      setTimeout(() => setNudging((s) => ({ ...s, [participantId]: false })), 6000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0118] text-white flex items-center justify-center" data-testid="host-loading">
        Loading host view…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#0D0118] text-white flex items-center justify-center p-6 text-center" data-testid="host-error">
        <div>
          <p className="text-2xl mb-2">Invalid host link</p>
          <p className="text-[#B49FCC]">{err}</p>
        </div>
      </div>
    );
  }

  const { session, station, groups_here } = data;
  const totalParticipants = groups_here.reduce((acc, g) => acc + (g.participants?.length || 0), 0);

  return (
    <div className="min-h-screen bg-[#0D0118] text-white" data-testid="host-station-view">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <p className="text-xs uppercase tracking-[0.18em] text-[#E91E8C] font-semibold mb-1">Station Host View</p>
          <h1 className="text-3xl md:text-5xl font-bold leading-tight" data-testid="host-station-name">
            {station.name || `Station ${station.order_index + 1}`}
          </h1>
          <p className="text-[#B49FCC] mt-2 text-sm md:text-base">
            {session.name} · Station {station.order_index + 1} of {session.num_stations} · Rotation {session.rotation_index + 1}
          </p>
        </motion.div>

        {/* Status row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-light rounded-xl p-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#8B5CF6]" />
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#B49FCC]">Teams here</p>
              <p className="text-xl font-bold" data-testid="host-teams-here-count">{groups_here.length}</p>
            </div>
          </div>
          <div className="glass-light rounded-xl p-3 flex items-center gap-2">
            <UserCircle className="w-4 h-4 text-[#00B0FF]" />
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#B49FCC]">Participants</p>
              <p className="text-xl font-bold" data-testid="host-participants-count">{totalParticipants}</p>
            </div>
          </div>
          <div className="glass-light rounded-xl p-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#FFB74D]" />
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#B49FCC]">Timer</p>
              <p className="text-xl font-bold tabular-nums" data-testid="host-timer">
                {timerText || (session.timer_active ? "—" : "Off")}
              </p>
            </div>
          </div>
          <div className="glass-light rounded-xl p-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#CCFF00]" />
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#B49FCC]">Status</p>
              <p className="text-base font-bold capitalize" data-testid="host-session-status">{session.status}</p>
            </div>
          </div>
        </div>

        {/* Instructions (collapsible) */}
        {station.instructions?.trim() && (
          <div className="glass-heavy rounded-2xl p-5 mb-6" data-testid="host-instructions">
            <button
              className="w-full flex items-center justify-between"
              onClick={() => setInstructionsOpen((v) => !v)}
              data-testid="host-instructions-toggle"
            >
              <span className="text-sm font-semibold text-[#E2D9F3] uppercase tracking-wide">Station Instructions</span>
              {instructionsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {instructionsOpen && (
              <p className="mt-3 whitespace-pre-wrap text-[#F5F0FA] leading-relaxed">{station.instructions}</p>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            data-testid="host-tab-group"
            onClick={() => setTab("group")}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "group" ? "bg-[#E91E8C] text-white" : "bg-white/5 text-[#B49FCC] hover:bg-white/10"}`}
          >
            Group notes
          </button>
          <button
            data-testid="host-tab-participants"
            onClick={() => setTab("participants")}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "participants" ? "bg-[#8B5CF6] text-white" : "bg-white/5 text-[#B49FCC] hover:bg-white/10"}`}
          >
            Participant notes
          </button>
        </div>

        {/* Group / Participant notes */}
        {groups_here.length === 0 ? (
          <div className="glass-light rounded-2xl p-8 text-center text-[#B49FCC]" data-testid="host-no-teams">
            No teams are at this station right now.
            <p className="text-xs mt-2">As facilitators rotate groups, the team(s) currently positioned here will appear automatically.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups_here.map((g) => {
              const gKey = noteKey(g.number, null);
              const members = g.participants || [];
              const totalAnswered = members.reduce((acc, p) => acc + (p.answer_progress?.answered || 0), 0);
              const totalExpected = members.reduce((acc, p) => acc + (p.answer_progress?.expected || 0), 0);
              const doneCount = members.filter((p) => {
                const ap = p.answer_progress;
                return ap && ap.expected > 0 && ap.answered >= ap.expected;
              }).length;
              const teamLevel = totalExpected === 0
                ? "none"
                : (totalAnswered === 0 ? "none" : (totalAnswered < totalExpected ? "partial" : "done"));
              return (
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-heavy rounded-2xl p-5"
                  data-testid={`host-group-card-${g.number}`}
                >
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <TeamAvatar teamNumber={g.number} avatarId={g.avatar_id} size="md" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-[#B49FCC]">Team {g.number}</p>
                        <p className="text-xl font-bold">{g.name?.trim() || `Team ${g.number}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-[#E2D9F3] flex items-center gap-1.5"
                        data-testid={`host-team-progress-${g.number}`}
                        title={`${doneCount} of ${members.length} member(s) finished all their questions`}
                      >
                        <StatusDot level={teamLevel} />
                        {totalExpected > 0 ? `${totalAnswered} / ${totalExpected} answers` : "No questions"}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-white/5 text-[#E2D9F3]">
                        {members.length} member{members.length === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>

                  {tab === "group" && (
                    <div data-testid={`host-group-note-block-${g.number}`}>
                      {members.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3" data-testid={`host-group-members-summary-${g.number}`}>
                          {members.map((p) => {
                            const status = progressStatus(p);
                            const canNudge = status.level !== "done" && (p.answer_progress?.expected || 0) > 0;
                            return (
                              <span
                                key={p.id}
                                className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-white/5"
                                title={`${p.name || "Unnamed"} · ${status.label}`}
                                data-testid={`host-member-pill-${p.id}`}
                                data-progress-level={status.level}
                              >
                                <StatusDot level={status.level} />
                                {p.is_team_lead && <Crown className="w-3 h-3 text-yellow-400" />}
                                <span className="text-[#F5F0FA]">{p.name?.trim() || "Unnamed"}</span>
                                {canNudge && (
                                  <button
                                    onClick={() => nudgeParticipant(p.id, p.name)}
                                    disabled={!!nudging[p.id]}
                                    className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FFB74D]/20 hover:bg-[#FFB74D]/35 text-[#FFB74D] disabled:opacity-40 transition-colors"
                                    title="Send a gentle nudge to this participant"
                                    data-testid={`host-nudge-pill-${p.id}`}
                                  >
                                    <Bell className="w-3 h-3" />
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <label className="text-xs uppercase tracking-wide text-[#B49FCC] flex items-center gap-2 mb-2">
                        <NotebookPen className="w-3.5 h-3.5" /> Observation note for the whole team
                      </label>
                      <textarea
                        rows={4}
                        value={drafts[gKey] ?? ""}
                        onChange={(e) => onDraftChange(gKey, e.target.value)}
                        placeholder="What did this team do well? Any insights, challenges, or quotes worth remembering?"
                        className="w-full input-dark rounded-xl p-3 text-sm leading-relaxed resize-y"
                        data-testid={`host-group-note-input-${g.number}`}
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={() => saveNote(g.number, null, "")}
                          disabled={saving[gKey]}
                          className="btn-primary-gradient control-btn text-sm px-4 py-2 disabled:opacity-50"
                          data-testid={`host-group-note-save-${g.number}`}
                        >
                          <Save className="w-4 h-4 mr-1 inline" />
                          {saving[gKey] ? "Saving…" : "Save note"}
                        </button>
                      </div>
                    </div>
                  )}

                  {tab === "participants" && (
                    <div className="space-y-3" data-testid={`host-participant-notes-${g.number}`}>
                      {(g.participants || []).length === 0 ? (
                        <p className="text-sm text-[#B49FCC]">No participants in this team yet.</p>
                      ) : (
                        g.participants.map((p) => {
                          const pKey = noteKey(g.number, p.id);
                          const status = progressStatus(p);
                          return (
                            <div key={p.id} className="bg-white/5 rounded-xl p-3" data-testid={`host-participant-row-${p.id}`} data-progress-level={status.level}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <StatusDot level={status.level} />
                                  {p.is_team_lead && <Crown className="w-4 h-4 text-yellow-400" />}
                                  <span className="font-semibold">{p.name?.trim() || "Unnamed"}</span>
                                  {p.is_team_lead && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-300">LEAD</span>
                                  )}
                                  <span
                                    className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#B49FCC]"
                                    data-testid={`host-participant-progress-${p.id}`}
                                  >
                                    {status.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {progressStatus(p).level !== "done" && (p.answer_progress?.expected || 0) > 0 && (
                                    <button
                                      onClick={() => nudgeParticipant(p.id, p.name)}
                                      disabled={!!nudging[p.id]}
                                      className="text-xs px-3 py-1 rounded-full bg-[#FFB74D]/15 hover:bg-[#FFB74D]/30 text-[#FFB74D] disabled:opacity-40 transition-colors flex items-center gap-1"
                                      title="Send a gentle nudge to this participant"
                                      data-testid={`host-nudge-${p.id}`}
                                    >
                                      <Bell className="w-3 h-3" />
                                      {nudging[p.id] ? "Sent" : "Nudge"}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => saveNote(g.number, p.id, p.name)}
                                    disabled={saving[pKey]}
                                    className="text-xs btn-ghost px-3 py-1 rounded-full disabled:opacity-50"
                                    data-testid={`host-participant-note-save-${p.id}`}
                                  >
                                    <Save className="w-3 h-3 mr-1 inline" />
                                    {saving[pKey] ? "Saving…" : "Save"}
                                  </button>
                                </div>
                              </div>
                              <textarea
                                rows={2}
                                value={drafts[pKey] ?? ""}
                                onChange={(e) => onDraftChange(pKey, e.target.value)}
                                placeholder={`Notes about ${p.name?.trim() || "this participant"}…`}
                                className="w-full input-dark rounded-lg p-2 text-sm resize-y"
                                data-testid={`host-participant-note-input-${p.id}`}
                              />
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        <p className="text-center text-[10px] text-[#6B5E7C] mt-8" data-testid="host-progress-legend">
          <span className="inline-flex items-center gap-1.5 mr-3"><StatusDot level="done" /> All answered</span>
          <span className="inline-flex items-center gap-1.5 mr-3"><StatusDot level="partial" /> In progress</span>
          <span className="inline-flex items-center gap-1.5 mr-3"><StatusDot level="none" /> Not started</span>
          <br className="md:hidden" />
          Tip: keep this tab open during the workshop — it auto-refreshes every few seconds. Notes are saved to the facilitator's records and exported in the PDF run-sheet.
        </p>
      </div>
    </div>
  );
};

export default HostStationView;
